import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import 'react-calendar-timeline/lib/Timeline.css';
import { useDispatch, useSelector } from 'react-redux';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import {
  isEmpty,
  isEqual,
  isUndefined,
  uniq,
  min,
  max,
  groupBy as _groupBy,
  isNull,
  sortBy,
} from 'underscore';
import Card from '@mui/material/Card';
import { CloseOutlined, ReportProblem } from '@mui/icons-material';
import Timeline, {
  TimelineHeaders,
  SidebarHeader,
  DateHeader,
  TimelineItem,
  TimelineGroup,
  ReactCalendarGroupRendererProps,
  ReactCalendarItemRendererProps,
  Unit,
} from 'react-calendar-timeline';
import { makeStyles } from '@mui/styles';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  CircularProgress,
  IconButton,
  alpha,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { add, endOfDay, startOfDay, sub } from 'date-fns';
import { Moment } from 'moment';

import { ItemTemplate } from '../TlItem';
import { Group } from '../Timeline/Group';
import './TLoverrides.css';
import { dispatch_theme, black } from '@/config/colors';
import VehicleDetail from '../../VehicleEditor/Detail';
import { routeUpdate, setPagination } from '@/actions/routes/routesPure';
import { AppDispatch, RootState } from '../../../index';
import WeightWatcher from '../../Common/Weights';
import { i18next } from '@/config/i18n';
import { Vehicle } from '@/config/types/Routes';
import { lazyRouteUpdate } from '@/actions/routes/routesThunks';
import { ExportDialog } from '../../Export/ExportDialog';
import { DownloadButton } from './Download/DownloadButton';
import { handleDownload } from './Download/downloadService';

import {
  Delivery,
  TimelineHighlight,
  Route,
  TimelineGroupCustom,
  TimelineItemCustom,
  TLShipments,
  TLStops,
} from '@/config/types/Routes';
import { usePrevious, isDepotReload as isDepotReloadCondition } from '@/hooks';

const keys = {
  groupIdKey: 'id',
  groupTitleKey: 'title',
  groupRightTitleKey: 'rightTitle',
  itemIdKey: 'id',
  itemTitleKey: 'title',
  itemDivTitleKey: 'title',
  itemGroupKey: 'group',
  itemTimeStartKey: 'start',
  itemTimeEndKey: 'end',
  groupLabelKey: 'title',
};

const formats: any = {
  year: {
    long: 'YYYY',
    mediumLong: 'YYYY',
    medium: 'YY',
    short: 'YY',
  },
  month: {
    long: 'MMMM',
    mediumLong: 'MMMM',
    medium: 'MM',
    short: 'MM',
  },
  week: {
    long: 'w',
    mediumLong: 'w',
    medium: 'w',
    short: 'w',
  },
  day: {
    long: 'dddd',
    mediumLong: 'ddd',
    medium: 'dd',
    short: 'dd',
  },
  hour: {
    long: 'HH:00',
    mediumLong: 'HH:00',
    medium: 'HH:00',
    short: 'HH:00',
  },
  minute: {
    long: 'mm',
    mediumLong: 'mm',
    medium: 'mm',
    short: 'mm',
  },
};

const useStyles = makeStyles((theme: typeof dispatch_theme) => ({
  sticky: {
    position: 'sticky',
    top: 0,
    zIndex: 800,
    backgroundColor: 'white !important',
  },
  fullHeight: {
    height: 45,
  },
  dateHeader: {
    color: theme.palette.primary.main + ' !important',
    height: '48px !important',
  },
  odd: {
    background: 'transparent !important',
  },
  group: {
    height: 45,
    background: 'transparent !important',
  },
  dateString: {
    background: 'transparent !important',
  },
  updatePlanningWarning: {
    color: '#ef5927',
  },
  updatePlanningWarningContainer: {
    display: 'flex',
    justifyContent: 'center',
    color: 'rgba(0, 0, 0, 0.6)',
  },
  updateModeIcon: {
    marginRight: '5px',
  },
  Dialogtitle: {
    background: 'rgba(239, 89, 39, 0.12)',
  },
  Dialogcontent: {
    padding: '0px',
  },
  DialogActionbtn: {
    marginTop: '14px',
  },
  ButtonStyle: {
    height: '40px',
    color: 'white',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '4px',
    marginRight: '16px',
  },
  closeIconBtn: {
    position: 'absolute',
    right: 12,
    top: 8,
    padding: 4,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '8px 16px',
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 56,
  },
}));

export type CustomTimelineProps = {
  height?: number | string;
};
export const CustomTimeline: React.FC<CustomTimelineProps> = (props) => {
  const { height } = props;
  const { grouping, selected_process, ui, optimization_data } = useSelector(
    (state: RootState) => state.core,
    isEqual
  );
  const {
    routes,
    colors,
    deleted,
    highlights,
    route_update,
    loading,
    routesAvailable: available,
    timelineRange: { startDate, endDate },
    pagination: {
      page,
      total_pages,
      num_results,
      objects: page_data,
      per_page,
    },
    deletedRoutes,
    reassignedShipments,
    isUpdatePlanningRunning,
    lockedVehicles,
  } = useSelector((state: RootState) => state.routes, isEqual);
  const prevSelectedProcessId = usePrevious(selected_process?.optimization_id);
  const dispatch = useDispatch<AppDispatch>();
  const [scrollEnd, setScrollEnd] = useState<boolean>(false);
  const [timelineHeight, setTimelineHeight] = useState<number>(
    window.innerHeight - 90
  );
  const [vehicleDetail, setVehicleDetail] = useState<Vehicle>(undefined);
  const [vehicle, setVehicle] = useState<Vehicle>(undefined);
  const [weightWatcher, setWeightWatcher] = useState<{
    open: boolean;
    data: any;
  }>({ open: false, data: undefined });
  const [groups, setGroups] = useState<TimelineGroup<TimelineGroupCustom>[]>(
    []
  );
  const [items, setItems] = useState<TimelineItem<TimelineItemCustom>[]>([]);
  const [routeId, setRouteId] = useState<number>(undefined);
  const [selected, setSelected] = useState({
    id: undefined,
    group: undefined,
    index: undefined,
  });
  const [filters, setFilter] = useState({
    filteredGroups: [],
    filteredItems: [],
  });
  const [visibleTimes, setVisibleTimes] = useState<{
    visibleTimeStart: number;
    visibleTimeEnd: number;
  }>({
    visibleTimeStart: new Date(startDate).valueOf(),
    visibleTimeEnd: new Date(endDate).valueOf(),
  });
  const [routeTourExport, setRouteTourExport] = useState(undefined);
  const [isExportOpen, setIsExportOpen] = useState<boolean>(false);

  const allRoutes = useMemo(() => Object.values(routes || {}), [routes]);
  const [movedItems, setMovedItems] = useState([]);

  const { visibleTimeEnd, visibleTimeStart } = visibleTimes;
  const scrollContainer: { current: HTMLDivElement } = useRef();
  const classes = useStyles();
  const clientHeight =
    (scrollContainer.current && scrollContainer.current.clientHeight) || null;
  const scrollHeight =
    (scrollContainer.current && scrollContainer.current.scrollHeight) || null;

  const isUpdatePlanningFlag = ui.includes('update_planning');

  const setBounds = useCallback(
    (usedGroups: TimelineGroup<TimelineGroupCustom>[]) => {
      const minimum = min(
        usedGroups,
        (group: TimelineGroup<TimelineGroupCustom>) =>
          new Date(group.route.start_time).valueOf()
      ) as TimelineGroup<TimelineGroupCustom>;
      const maximum = max(
        usedGroups,
        (group: TimelineGroup<TimelineGroupCustom>) =>
          new Date(group.route.end_time).valueOf()
      ) as TimelineGroup<TimelineGroupCustom>;
      const start = sub(new Date(minimum.route.start_time), { hours: 1 });
      const end = add(new Date(maximum.route.end_time), { hours: 1 });
      handleTimeChange(start.valueOf(), end.valueOf());
    },
    []
  );

  function getRouteBounds(route: Route) {
    const stops = route.stops || [];
    const firstStopDep = stops.length > 0 ? stops[0].departure_time : null;

    let endMs: number | null = null;

    if (stops.length > 0) {
      const lastStop = stops[stops.length - 1];
      const isDepotOrGarage =
        lastStop.type === 'depot' || lastStop.type === 'garage';

      if (isDepotOrGarage) {
        endMs = new Date(lastStop.arrival_time ?? route.end_time).valueOf();
      } else if (lastStop.arrival_time) {
        endMs = new Date(lastStop.end_time ?? route.end_time).valueOf();
      }
    }

    const startMs = new Date(firstStopDep ?? route.start_time).valueOf();
    const finalEndMs = endMs ?? new Date(route.end_time).valueOf();

    return { startMs, endMs: finalEndMs };
  }

  const getVehiclesByName = (vehicle_name: string) => {
    if (!optimization_data || !optimization_data.vehicles) {
      return null;
    }
    const vehicles = optimization_data.vehicles.filter(
      (vehicle: Vehicle) => vehicle.name === vehicle_name
    );
    return vehicles.length > 0 ? vehicles[0] : null;
  };
  const showVehicle = useCallback(
    (vehicle_name: string) => {
      const vehicle_details = getVehiclesByName(vehicle_name);
      setVehicle(vehicle_details);
      setVehicleDetail(vehicle_details);
    },
    [optimization_data, setVehicle, setVehicleDetail, setWeightWatcher]
  );

  const toggleWeightWatcher = useCallback(
    (group: TimelineGroup<TimelineGroupCustom>) => {
      const vehicle_details = getVehiclesByName(group?.route?.vehicle_name);
      setVehicle(vehicle_details);
      setWeightWatcher({ open: true, data: group });
    },
    [setVehicle, setWeightWatcher]
  );
  const groupData = useMemo(() => {
    const sortedGroups = sortBy(groups, 'id');
    return filters.filteredGroups.length
      ? sortedGroups.filter(({ route: { id } }) =>
          filters.filteredGroups.includes(id)
        )
      : sortedGroups;
  }, [filters, groups]);
  // TODO: move to custom hook and split
  const setTimelineData = useCallback(
    (replace = false, source: Route[]) => {
      const grouped = _groupBy(source, 'vehicle_name');
      const groupKeys = Object.keys(grouped);
      //@ts-ignore
      const itemsData: TimelineItem<TimelineItemCustom>[] = groupKeys.flatMap(
        (vehicleName) =>
          grouped[vehicleName].flatMap((route, routeIndex) =>
            route.stops.flatMap((stop, stopIndex) => {
              if (stop.type === 'depot') return [];

              const stopDeliveries = route.shipments.filter(
                (delivery) =>
                  stop.shipments_to_load?.includes(delivery.custom_id) ||
                  stop.shipments_to_unload?.includes(delivery.custom_id)
              );
              return stopDeliveries.map((delivery) => {
                const arrival = new Date(
                  stop?.arrival_time || route.start_time
                ).valueOf();
                const departure = new Date(
                  stop?.departure_time || route.end_time
                ).valueOf();

                return {
                  id: `${delivery.custom_id}_${route.id}`,
                  group: vehicleName,
                  delivery,
                  allShipments: stopDeliveries,
                  stop,
                  route_id: route.id,
                  canMove: isUpdatePlanningFlag,
                  canChangeGroup: isUpdatePlanningFlag,
                  isUpdatePlanningFlag,
                  onClick: () =>
                    setSelected({
                      id: route.vehicle_name || delivery.multi,
                      group: route.id,
                      index: stopIndex,
                    }),
                  start: arrival,
                  end: departure,
                  itemProps: {
                    type: 'main',
                  },
                };
              });
            })
          )
      );
      const groupsData: TimelineGroup<TimelineGroupCustom>[] = groupKeys.map(
        (vehicleName) => {
          const routes = grouped[vehicleName];

          const totalStops = routes.reduce(
            (acc, route) =>
              acc +
              route.stops.filter(
                (stop) => stop.type !== 'depot' && stop.type !== 'garage'
              ).length,
            0
          );

          const totalShipments = routes.reduce(
            (acc, route) => acc + route.shipments.length,
            0
          );

          return {
            id: vehicleName,
            route: routes[0],
            title: vehicleName,
            color: colors[routes[0].id]?.color,
            total_routes: routes.map((r) => r.id),
            toggleWeightWatcher,
            showVehicle,
            showTourExport: () => setRouteTourExport(routes),
            update: route_update?.old_id,
            display: {
              totalStops,
              totalShipments,
            },
            isLockedVehicle: lockedVehicles.includes(routes[0]?.vehicle_id),
          };
        }
      );
      // ---- Connector direct loads ----
      const midOfStop = (st: any, route: Route) => {
        const arr = new Date(
          st?.arrival_time || st?.departure_time || route.start_time
        ).valueOf();
        const dep = new Date(
          st?.departure_time || st?.arrival_time || route.end_time
        ).valueOf();
        return Math.round((arr + dep) / 2);
      };

      const connectors: TimelineItem<TimelineItemCustom>[] = groupKeys.flatMap(
        (vehicleName) =>
          grouped[vehicleName].flatMap((route: Route) => {
            const loadMap = new Map<string, { st: any; idx: number }>();
            const unloadMap = new Map<string, { st: any; idx: number }>();
            route.stops.forEach((st, idx) => {
              st?.shipments_to_load?.forEach((id: string) =>
                loadMap.set(id, { st, idx })
              );
              st?.shipments_to_unload?.forEach((id: string) =>
                unloadMap.set(id, { st, idx })
              );
            });

            const candidates = route.shipments.filter(
              (s: any) => !!s?.direct_ref_custom_id
            );
            const spanByPickup = new Map<
              string,
              { start: number; end: number }
            >();

            candidates.forEach((del: any) => {
              const load = loadMap.get(del.custom_id);
              const unl = unloadMap.get(del.direct_ref_custom_id);

              if (!load || !unl) {
                console.warn('DL pair missing stop', {
                  routeId: route.id,
                  del: del.custom_id,
                  pickupRef: del.direct_ref_custom_id,
                  hasPickup: !!load,
                  hasDelivery: !!unl,
                });
                return;
              }

              const start = midOfStop(load.st, route);
              const end = midOfStop(unl.st, route);
              if (end <= start) return;

              const key = `${route.id}_${load.idx}`; // per-pickup merge
              const cur = spanByPickup.get(key) || { start, end };
              cur.start = Math.min(cur.start, start);
              cur.end = Math.max(cur.end, end);
              spanByPickup.set(key, cur);
            });

            return Array.from(spanByPickup.entries()).map(
              ([key, span]) =>
                ({
                  id: `connector_${key}`,
                  group: vehicleName,
                  route_id: route.id,
                  start: span.start,
                  end: span.end,
                  canMove: false,
                  canChangeGroup: false,
                  itemProps: {
                    type: 'connector',
                    style: {
                      background: 'transparent',
                      border: 'none',
                      position: 'absolute',
                      height: 10,
                      top: '1%',
                      transform: 'translateY(-50%)',
                      zIndex: 2000,
                    },
                  },
                }) as TimelineItem<TimelineItemCustom>
            );
          })
      );
      //@ts-ignore
      const path: TimelineItem<TimelineItemCustom>[] = groupKeys.flatMap(
        (key) =>
          grouped[key]
            .map((route: Route, i: number) => {
              const { startMs, endMs } = getRouteBounds(route);
              if (!startMs || !endMs || startMs >= endMs) return null;
              const start = startMs;
              const end = endMs;
              return {
                id: `${route.id}_path_${i}_${key}`,
                group: `${key}`,
                canMove: false,
                canChangeGroup: false,
                route_id: route.id,
                start,
                end,
                itemProps: {
                  type: 'path',
                  style: {
                    background: '#868686',
                    borderBottom: 'none',
                    height: 2,
                    top: '50%',
                    zIndex: -1,
                  },
                },
              };
            })
            .filter(Boolean)
      );

      const startEndItems = groupKeys.flatMap((key) =>
        grouped[key].flatMap((route: Route, routeIndex) => {
          const routeId = route.id;
          const stops = route.stops;
          const { startMs, endMs } = getRouteBounds(route);
          const lastStop = [...stops]
            .reverse()
            .find((stop) => stop.arrival_time);
          const isGarageStop = lastStop?.type === 'garage';

          const markerStyle = isGarageStop
            ? {
                backgroundColor: 'transparent',
                border: `2px dashed ${black}`,
                width: '2px',
                height: '80%',
                top: '10%',
                zIndex: 10,
              }
            : {
                backgroundColor: '#000',
                border: 'none',
                width: '2px',
                height: '80%',
                top: '10%',
                zIndex: 10,
              };

          const markers: TimelineItem<TimelineItemCustom>[] = [
            //@ts-ignore
            {
              id: `${routeId}_start_${routeIndex}`,
              group: `${key}`,
              canMove: false,
              canChangeGroup: false,
              route_id: routeId,
              start: startMs,
              end: startMs,
              itemProps: {
                type: 'start',
                style: {
                  backgroundColor: '#000',
                  width: '2px',
                  border: 'none',
                },
              },
            },
            //@ts-ignore
            {
              id: `${routeId}_end_${routeIndex}`,
              group: `${key}`,
              canMove: false,
              canChangeGroup: false,
              route_id: routeId,
              start: endMs,
              end: endMs,
              itemProps: {
                type: 'end',
                style: markerStyle,
              },
            },
          ];
          return markers;
        })
      );

      const newGroups = replace
        ? groupsData
        : uniq([...groups, ...groupsData], (x) => x.id);
      const newItems = replace
        ? [...itemsData, ...path, ...startEndItems, ...connectors]
        : uniq(
            [...items, ...itemsData, ...path, ...startEndItems, ...connectors],
            (x) => x.id
          );

      setGroups(newGroups);
      setItems(newItems);
      if (newGroups.length > 0) {
        setBounds(newGroups);
      }
    },
    [
      colors,
      grouping,
      groups,
      items,
      setBounds,
      setSelected,
      showVehicle,
      toggleWeightWatcher,
      route_update?.old_id,
      lockedVehicles,
      movedItems,
      isUpdatePlanningFlag,
    ]
  );

  useEffect(() => {
    const resizeHandler = () => setTimelineHeight(window.innerHeight - 200);
    window.addEventListener('resize', resizeHandler);

    return () => {
      setPagination(undefined);
      setItems([]);
      setGroups([]);
      setVisibleTimes({
        visibleTimeStart: startOfDay(new Date()).valueOf(),
        visibleTimeEnd: endOfDay(new Date()).valueOf(),
      });
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);

  useEffect(() => {
    if (!highlights.length) {
      return setFilter({ filteredGroups: [], filteredItems: [] });
    }
    const filteredGroups: TimelineHighlight[] = highlights.map(
      ({ id }: TimelineHighlight) => id
    );
    const filteredItems = highlights
      .flatMap(({ deliveries }: TimelineHighlight) => deliveries)
      .map(({ id, multi }: Delivery) => id || multi);
    setFilter({ filteredGroups, filteredItems });
  }, [highlights]);

  const handleScroll = useCallback(({ target }: Event & { target: any }) => {
    const scrollTop: number = Math.round(target.scrollTop);
    const newScrollHeight: number = Math.round(target.scrollHeight);
    const newClientHeight: number = Math.round(target.clientHeight);
    const scrollDiff: number = newScrollHeight - scrollTop;
    const newScrollEnd: boolean = scrollDiff <= newClientHeight;
    if (newScrollEnd) {
      setScrollEnd(newScrollEnd);
    }
  }, []);

  useEffect(() => {
    if (scrollContainer.current) {
      scrollContainer.current.addEventListener('scroll', handleScroll);
    }
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollContainer, handleScroll]);

  useEffect(() => {
    if (scrollHeight && clientHeight && !highlights.length) {
      if (scrollHeight <= clientHeight && page < total_pages) {
        setScrollEnd(true);
      }
    }
  }, [scrollHeight, page, total_pages, highlights, clientHeight]);

  useEffect(() => {
    if (scrollEnd) {
      const onSetPagination = (page: number) => dispatch(lazyRouteUpdate(page));
      onSetPagination(page + 1);
      setScrollEnd(false);
    }
  }, [scrollEnd, page, dispatch]);

  useEffect(() => {
    if (page && prevSelectedProcessId !== selected_process?.optimization_id) {
      const replace = page_data.length > per_page;
      const source = page_data.map((id: number) => routes[id]).filter(Boolean);
      source.length > 0 && setTimelineData(replace, source);
    }
  }, [
    page_data,
    page,
    routes,
    prevSelectedProcessId,
    selected_process?.optimization_id,
    per_page,
    setTimelineData,
  ]);

  useEffect(() => {
    if (selected_process) {
      setItems([]);
      setGroups([]);
    }
  }, [
    selected_process?.optimization_id,
    setItems,
    setGroups,
    selected_process,
  ]);

  useEffect(() => {
    if (!page) {
      setGroups([]);
      setItems([]);
    }
  }, [page, setGroups, setItems]);

  useEffect(() => {
    routes.length > 0 && setTimelineData(true, Object.values(routes));
  }, [routes, setTimelineData]);

  useEffect(() => {
    const hasUpdate = !isUndefined(route_update);
    if (isEmpty(routes)) {
      setGroups([]);
      setItems([]);
    } else {
      // cached input data gets rendered.
      setTimelineData(hasUpdate, Object.values(routes));
    }
    if (hasUpdate) {
      dispatch(routeUpdate(undefined));
    }
    // TODO: check what deps are really needed
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes, route_update]);

  function handleTimeChange(
    newVisibleTimeStart: number,
    newVisibleTimeEnd: number
  ) {
    setVisibleTimes({
      visibleTimeStart: newVisibleTimeStart,
      visibleTimeEnd: newVisibleTimeEnd,
    });
  }
  const itemData = useMemo(() => {
    if (filters.filteredItems.length) {
      return filters.filteredGroups
        .flatMap((id) => items.filter((item) => item.route_id === id))
        .map((data) => ({
          ...data,
          itemProps: {
            ...data.itemProps,
            style: {
              ...data.itemProps.style,
              ...{ opacity: filters.filteredItems.includes(data.id) ? 1 : 0.5 },
            },
          },
        }));
    }
    return items;
  }, [items, filters.filteredGroups, filters.filteredItems]);
  const de = i18next.language === 'de';
  return (
    <DndProvider backend={HTML5Backend}>
      <Card
        style={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          overflow: 'hidden',
          margin: '13px 15px 22px 15px',
        }}
      >
        <div className={classes.toolbar}>
          <DownloadButton
            onDownload={(type) => {
              if (type === 'pdf') {
                setIsExportOpen(true);
              } else {
                handleDownload(type, selected_process?.optimization_id);
              }
            }}
          />
        </div>
        {Boolean(groupData.length && itemData.length) && (
          <>
            {deletedRoutes?.[selected_process?.optimization_id]?.length > 0 && (
              <h3 className={classes.updatePlanningWarningContainer}>
                {!isUpdatePlanningRunning ? (
                  <>
                    <ReportProblem
                      color="warning"
                      className={classes.updateModeIcon}
                    />
                    {
                      <p
                        className={classes.updatePlanningWarning}
                      >{`${i18next.t('common:routes.updatePlanningMode')}`}</p>
                    }
                  </>
                ) : (
                  <>
                    <CircularProgress
                      size={20}
                      style={{ marginRight: dispatch_theme.spacing(2) }}
                    />
                    <p>{i18next.t('common:routes.planningIsUpdating')}</p>
                  </>
                )}
              </h3>
            )}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <Timeline
                data-cy={'timeline'}
                groups={groupData}
                items={itemData}
                keys={keys}
                lineHeight={45}
                itemHeightRatio={0.75}
                sidebarWidth={270}
                {...(isUpdatePlanningFlag ? {} : { selected: [] })}
                onTimeChange={handleTimeChange}
                stackItems={false}
                canChangeGroup={isUpdatePlanningFlag}
                canMove={isUpdatePlanningFlag}
                canResize={false}
                horizontalLineClassNamesForGroup={() => [classes.odd]}
                //TODO: check if those vars are needed
                //eslint-disable-next-line no-unused-vars
                verticalLineClassNamesForTime={(timeStart, timeEnd) => [
                  'weekday',
                ]}
                itemRenderer={(
                  props: ReactCalendarItemRendererProps<TimelineItemCustom>
                ) => <ItemTemplate {...props} />}
                groupRenderer={(
                  props: ReactCalendarGroupRendererProps<TimelineGroupCustom>
                ) => <Group {...props} />}
                visibleTimeStart={visibleTimeStart}
                visibleTimeEnd={visibleTimeEnd}
              >
                <TimelineHeaders className={classes.sticky}>
                  <SidebarHeader>
                    {({ getRootProps }) => {
                      const { style } = getRootProps();
                      return (
                        <div
                          style={{
                            ...style,
                            borderBottom: '1px solid #bbb',
                            borderTop: '1px solid #bbb',
                            padding: dispatch_theme.spacing(2, 1),
                            borderTopLeftRadius: 4,
                            maxWidth: 270,
                            width: 270,
                          }}
                        >
                          <Grid
                            container
                            alignItems="center"
                            justifyContent={'center'}
                            className={classes.fullHeight}
                          >
                            <Typography color={'primary'}>
                              {groupData.length}{' '}
                              {i18next.t('common:routes.vehicles', {
                                multi: num_results > 1 ? (de ? 'en' : 's') : '',
                              })}
                            </Typography>
                          </Grid>
                        </div>
                      );
                    }}
                  </SidebarHeader>
                  <DateHeader
                    unit="primaryHeader"
                    className={classes.dateHeader}
                  />
                  <DateHeader
                    className={classes.dateString}
                    labelFormat={(startEnd: [Moment, Moment], unit: Unit) =>
                      startEnd[0].format(formats[unit].short)
                    }
                  />
                </TimelineHeaders>
              </Timeline>
            </div>
          </>
        )}
        {loading && (
          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            style={{ marginBottom: 50, marginTop: 50 }}
          >
            <CircularProgress
              size={20}
              style={{ marginLeft: dispatch_theme.spacing(2) }}
            />
          </Grid>
        )}
        {(() => {
          return (
            weightWatcher.open && (
              //@ts-ignore
              <WeightWatcher
                vehicle={vehicle}
                open={!isEmpty(vehicle) && weightWatcher.open}
                close={() => {
                  setVehicle(undefined);
                  setWeightWatcher({ open: false, data: undefined });
                }}
                route={
                  Array.isArray(weightWatcher.data?.total_routes)
                    ? weightWatcher.data.total_routes
                        .map((id: number) => routes[id])
                        .filter(Boolean)
                    : weightWatcher.data?.route
                    ? [weightWatcher.data.route]
                    : []
                }
                routeId={routeId}
              />
            )
          );
        })()}
        {vehicleDetail && (
          <Dialog
            open={!isUndefined(vehicle)}
            maxWidth={'md'}
            fullWidth={true}
            onClose={() => setVehicleDetail(undefined)}
          >
            <DialogTitle className={classes.Dialogtitle}>
              {vehicleDetail.name || ''}
              <IconButton
                aria-label="close"
                onClick={() => setVehicleDetail(undefined)}
                className={classes.closeIconBtn}
              >
                <CloseOutlined style={{ color: '#000000' }} />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <VehicleDetail
                vehicle={vehicleDetail}
                disabled={true}
                header={false}
                onChange={() => null}
              />
            </DialogContent>
            <DialogActions className={classes.DialogActionbtn}></DialogActions>
          </Dialog>
        )}
        <ExportDialog
          open={isExportOpen}
          onClose={() => setIsExportOpen(false)}
          routes={allRoutes}
          title={i18next.t('common:routes.printAllDay')}
        />
        <ExportDialog
          open={!!routeTourExport}
          onClose={() => setRouteTourExport(undefined)}
          routes={routeTourExport ? [...routeTourExport] : []}
        />
      </Card>
      {/* {!!Object.keys(shipmentsFromDeletedRoutes).length && <FreeShipmentsList orders={freeOrders} loading={areFreeShipmentsLoading} />} */}
    </DndProvider>
  );
};
